import React from 'react';
import { Box, Alert, AlertTitle, Slide, LinearProgress, IconButton, Collapse } from '@mui/material';
import { styled, alpha, keyframes } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { TransitionGroup } from 'react-transition-group';
import useNotificationStore from '../../../stores/notificationStore';

// Animation keyframes
const slideIn = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Styled Alert with elegant glassmorphism design
const StyledAlert = styled(Alert)(({ theme, severity }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  const colors = {
    success: {
      primary: '#10b981',
      secondary: '#065f46',
      background: isDarkMode ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
      text: isDarkMode ? '#34d399' : '#065f46'
    },
    error: {
      primary: '#ef4444',
      secondary: '#991b1b',
      background: isDarkMode ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
      text: isDarkMode ? '#fca5a5' : '#991b1b'
    },
    warning: {
      primary: '#f59e0b',
      secondary: '#92400e',
      background: isDarkMode ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)',
      text: isDarkMode ? '#fcd34d' : '#92400e'
    },
    info: {
      primary: '#3b82f6',
      secondary: '#1e3a8a',
      background: isDarkMode ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
      text: isDarkMode ? '#93bbfe' : '#1e3a8a'
    }
  };

  const color = colors[severity] || colors.info;

  return {
    // Glassmorphism effect
    backgroundColor: isDarkMode 
      ? alpha(theme.palette.background.paper, 0.9)
      : alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    
    // Subtle border
    border: `1px solid ${alpha(color.primary, 0.2)}`,
    borderRadius: theme.spacing(2),
    
    // Elegant shadows
    boxShadow: `
      0 0 0 1px ${alpha(color.primary, 0.1)},
      0 2px 8px ${alpha(color.primary, 0.08)},
      0 16px 24px ${alpha(theme.palette.common.black, 0.09)},
      0 24px 32px ${alpha(theme.palette.common.black, 0.06)}
    `,
    
    // Spacing and typography
    padding: theme.spacing(2, 2.5),
    fontSize: '0.925rem',
    fontWeight: 400,
    letterSpacing: '0.01em',
    
    // Smooth animation
    animation: `${fadeIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1)`,
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    
    // Width
    width: '100%',
    minWidth: 340,
    maxWidth: 500,
    
    // Margin between stacked notifications
    marginBottom: theme.spacing(1),
    
    // Icon styling
    '& .MuiAlert-icon': {
      color: color.primary,
      fontSize: '1.25rem',
      marginRight: theme.spacing(1.5),
      opacity: 0.9
    },
    
    // Message styling
    '& .MuiAlert-message': {
      padding: 0,
      color: theme.palette.text.primary,
      lineHeight: 1.5
    },
    
    // Title styling
    '& .MuiAlertTitle-root': {
      color: color.text,
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      fontSize: '1rem'
    },
    
    // Close button styling
    '& .MuiAlert-action': {
      color: alpha(theme.palette.text.primary, 0.7),
      marginRight: -theme.spacing(0.5),
      paddingLeft: theme.spacing(2),
      '& .MuiIconButton-root': {
        borderRadius: theme.spacing(1),
        '&:hover': {
          backgroundColor: alpha(color.primary, 0.08)
        }
      }
    },
    
    // Hover effect
    '&:hover': {
      boxShadow: `
        0 0 0 1px ${alpha(color.primary, 0.15)},
        0 2px 8px ${alpha(color.primary, 0.12)},
        0 16px 24px ${alpha(theme.palette.common.black, 0.12)},
        0 24px 32px ${alpha(theme.palette.common.black, 0.09)}
      `,
      transform: 'translateY(-1px)'
    },
    
    // Colored accent bar
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: color.primary,
      borderRadius: `${theme.spacing(2)} 0 0 ${theme.spacing(2)}`
    }
  };
});

// Individual notification component
const NotificationItem = ({ notification, onClose }) => {
  const [progress, setProgress] = React.useState(0);
  const [isExiting, setIsExiting] = React.useState(false);
  
  // Parse message and title
  const [title, message] = React.useMemo(() => {
    if (notification.message && notification.message.includes('\n')) {
      const parts = notification.message.split('\n');
      return [parts[0], parts.slice(1).join('\n')];
    }
    return [null, notification.message];
  }, [notification.message]);
  
  // Progress bar for auto-dismiss
  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setIsExiting(true);
          setTimeout(() => onClose(notification.id), 300);
          return 100;
        }
        return prev + (100 / (notification.autoHideDuration / 100));
      });
    }, 100);
    
    return () => clearInterval(timer);
  }, [notification.id, notification.autoHideDuration, onClose]);
  
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(notification.id), 300);
  };

  return (
    <Collapse in={!isExiting}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <StyledAlert
          severity={notification.severity}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {title && <AlertTitle>{title}</AlertTitle>}
          <Box sx={{ wordBreak: 'break-word' }}>{message}</Box>
        </StyledAlert>
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            backgroundColor: 'transparent',
            borderRadius: '0 0 16px 16px',
            overflow: 'hidden',
            '& .MuiLinearProgress-bar': {
              backgroundColor: theme => alpha(theme.palette.text.primary, 0.3),
              transition: 'transform 0.1s linear'
            }
          }}
        />
      </Box>
    </Collapse>
  );
};

/**
 * Stacked notifications component that displays multiple notifications
 */
const StackedNotifications = () => {
  const { notificationStack, removeFromStack } = useNotificationStore();
  
  // Limit to 5 visible notifications
  const visibleNotifications = notificationStack.slice(-5);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'flex-end',
        pointerEvents: 'none',
        '& > *': {
          pointerEvents: 'auto'
        }
      }}
    >
      <TransitionGroup>
        {visibleNotifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={removeFromStack}
          />
        ))}
      </TransitionGroup>
    </Box>
  );
};

export default StackedNotifications;